import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFloatingTree,
    useHover,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
    useTypeahead,
  } from "@floating-ui/react";
  import * as React from "react";
  import { Link } from "react-router-dom";
  import PropTypes from "prop-types";
  
  var __rest =
    (this && this.__rest) ||
    function (s, e) {
      var t = {};
      for (var p in s)
        if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
          t[p] = s[p];
      if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (
            e.indexOf(p[i]) < 0 &&
            Object.prototype.propertyIsEnumerable.call(s, p[i])
          )
            t[p[i]] = s[p[i]];
        }
      return t;
    };
  const MenuContext = React.createContext({
    getItemProps: () => ({}),
    activeIndex: null,
    setActiveIndex: () => {},
    setHasFocusInside: () => {},
    isOpen: false,
  });
  const DropdownComponent = React.forwardRef((_a, forwardedRef) => {
    var { children, label, disabled, direction, className = "" } = _a,
      props = __rest(_a, [
        "children",
        "label",
        "disabled",
        "direction",
        "className",
      ]);
    const [isOpen, setIsOpen] = React.useState(false);
    const [hasFocusInside, setHasFocusInside] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(null);
    const elementsRef = React.useRef([]);
    const labelsRef = React.useRef([]);
    const parent = React.useContext(MenuContext);
    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const item = useListItem();
    const isNested = parentId != null;
    const { floatingStyles, refs, context } = useFloating({
      nodeId,
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: isNested
        ? "right-start"
        : direction
        ? direction
        : "bottom-start",
      middleware: [
        offset({ mainAxis: isNested ? 0 : 4, alignmentAxis: isNested ? -4 : 0 }),
        flip({
          fallbackAxisSideDirection: 'start',
        }),
        shift(),
      ],
      whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, {
      enabled: isNested,
      delay: { open: 75 },
      handleClose: safePolygon({ blockPointerEvents: true }),
    });
    const click = useClick(context, {
      event: "mousedown",
      toggle: !isNested,
      ignoreMouse: isNested,
    });
    const role = useRole(context, { role: "menu" });
    const dismiss = useDismiss(context, { bubbles: true });
    const listNavigation = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      nested: isNested,
      onNavigate: setActiveIndex,
    });
    const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      onMatch: isOpen ? setActiveIndex : undefined,
      activeIndex,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
      [hover, click, role, dismiss, listNavigation, typeahead]
    );
  
    React.useEffect(() => {
      if (!tree) return;
      function handleTreeClick() {
        setIsOpen(false);
      }
      function onSubMenuOpen(event) {
        if (event.nodeId !== nodeId && event.parentId === parentId) {
          setIsOpen(false);
        }
      }
      tree.events.on("click", handleTreeClick);
      tree.events.on("menuopen", onSubMenuOpen);
      return () => {
        tree.events.off("click", handleTreeClick);
        tree.events.off("menuopen", onSubMenuOpen);
      };
    }, [tree, nodeId, parentId]);
    React.useEffect(() => {
      if (isOpen && tree) {
        tree.events.emit("menuopen", { parentId, nodeId });
      }
    }, [tree, isOpen, nodeId, parentId]);
    return React.createElement(
      FloatingNode,
      { id: nodeId },
      React.createElement(
        "button",
        Object.assign(
          {
            ref: useMergeRefs([refs.setReference, item.ref, forwardedRef]),
            tabIndex: !isNested
              ? undefined
              : parent.activeIndex === item.index
              ? 0
              : -1,
            role: isNested ? "menuitem" : undefined,
            "data-open": isOpen ? "" : undefined,
            "data-nested": isNested ? "" : undefined,
            "data-focus-inside": hasFocusInside ? "" : undefined,
            className: isNested
              ? `mu-dropdown-item ${disabled ? "mu-disable-item" : ""}`
              : `mu-dropdown-root ${
                  disabled ? "mu-disable-item" : ""
                } ${className}`,
          },
          getReferenceProps(
            parent.getItemProps(
              Object.assign(Object.assign({}, props), {
                onFocus(event) {
                  var _a;
                  (_a = props.onFocus) === null || _a === void 0
                    ? void 0
                    : _a.call(props, event);
                  setHasFocusInside(false);
                  parent.setHasFocusInside(true);
                },
              })
            )
          )
        ),
        label,
        isNested &&
          React.createElement(
            "span",
            { "aria-hidden": true, style: { marginLeft: 10, fontSize: 10 } },
            "\u25B6"
          )
      ),
      React.createElement(
        MenuContext.Provider,
        {
          value: {
            activeIndex,
            setActiveIndex,
            getItemProps,
            setHasFocusInside,
            isOpen,
          },
        },
        React.createElement(
          FloatingList,
          { elementsRef: elementsRef, labelsRef: labelsRef },
          isOpen &&
            React.createElement(
              FloatingPortal,
              null,
              React.createElement(
                FloatingFocusManager,
                {
                  context: context,
                  modal: false,
                  initialFocus: isNested ? -1 : 0,
                  returnFocus: !isNested,
                },
                React.createElement(
                  "div",
                  Object.assign(
                    {
                      ref: refs.setFloating,
                      className: "mu-dropdown",
                      style: floatingStyles,
                    },
                    getFloatingProps()
                  ),
                  children
                )
              )
            )
        )
      )
    );
  });
  export const DropdownLink = React.forwardRef((_a, forwardedRef) => {
    var { label, disabled, className = "" } = _a,
      props = __rest(_a, ["label", "disabled", "className"]);
    const menu = React.useContext(MenuContext);
    const item = useListItem({ label: disabled ? null : label });
    const tree = useFloatingTree();
    const isActive = item.index === menu.activeIndex;
    return React.createElement(
      Link,
      Object.assign(
        {},
        props,
        {
          ref: useMergeRefs([item.ref, forwardedRef]),
          type: "link",
          role: "menuitem",
          className: `mu-dropdown-item ${
            disabled ? "mu-disable-item" : ""
          } ${className}`,
          to: props.href,
          tabIndex: isActive ? 0 : -1,
        },
        menu.getItemProps({
          onClick(event) {
            var _a;
            (_a = props.onClick) === null || _a === void 0
              ? void 0
              : _a.call(props, event);
            tree === null || tree === void 0 ? void 0 : tree.events.emit("click");
          },
          onFocus(event) {
            var _a;
            (_a = props.onFocus) === null || _a === void 0
              ? void 0
              : _a.call(props, event);
            menu.setHasFocusInside(true);
          },
        })
      ),
      label
    );
  });
  export const DropdownToggle = React.forwardRef((props, ref) => {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
      return React.createElement(
        FloatingTree,
        null,
        React.createElement(
          DropdownComponent,
          Object.assign({}, props, { ref: ref })
        )
      );
    }
    return React.createElement(
      DropdownComponent,
      Object.assign({}, props, { ref: ref })
    );
  });
  
  export const DropdownButton = React.forwardRef((_a, forwardedRef) => {
    var { label, disabled, className = "" } = _a,
      props = __rest(_a, ["label", "disabled", "className"]);
    const menu = React.useContext(MenuContext);
    const item = useListItem({ label: disabled ? null : label });
    const tree = useFloatingTree();
    const isActive = item.index === menu.activeIndex;
    return React.createElement(
      "button",
      Object.assign(
        {},
        props,
        {
          ref: useMergeRefs([item.ref, forwardedRef]),
          type: "link",
          role: "menuitem",
          className: `mu-dropdown-item ${
            disabled ? "mu-disable-item" : ""
          } ${className}`,
          onClick: props.onClick,
          tabIndex: isActive ? 0 : -1,
          disabled: disabled,
        },
        menu.getItemProps({
          onClick(event) {
            var _a;
            (_a = props.onClick) === null || _a === void 0
              ? void 0
              : _a.call(props, event);
            tree === null || tree === void 0 ? void 0 : tree.events.emit("click");
          },
          onFocus(event) {
            var _a;
            (_a = props.onFocus) === null || _a === void 0
              ? void 0
              : _a.call(props, event);
            menu.setHasFocusInside(true);
          },
        })
      ),
      label
    );
  });
  
  DropdownButton.propTypes = {
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
  };
  
  DropdownLink.propTypes = {
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    href: PropTypes.string,
  };
  
  DropdownComponent.propTypes = {
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    direction: PropTypes.string,
    children: PropTypes.array.isRequired,
    className: PropTypes.string,
  };